import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { callListUsers, type AdminUser } from '../firebase';
import { useSort, type SortValue } from '../sorting';
import { isSuspiciousUser } from '../suspicious';
import { buildUsersCsv } from './usersCsv';
import { downloadCsv, csvFilename } from '../csv';

// Paginamos sobre Auth con `nextPageToken`. La búsqueda es client-side sobre la
// página cargada (1000 max por llamada) — suficiente mientras el padrón sea
// pequeño. Si crece, mover a `getUserByEmail` server-side.

type SortKey = 'name' | 'email' | 'credits' | 'reports' | 'created' | 'lastLogin';

// Valor comparable por columna. Fechas → ms epoch; strings → minúsculas para
// orden case-insensitive; nulls → null (van siempre al final).
function sortValue(u: AdminUser, key: SortKey): SortValue {
  switch (key) {
    case 'name':
      return u.displayName?.toLowerCase() ?? null;
    case 'email':
      return u.email?.toLowerCase() ?? null;
    case 'credits':
      return u.credits ?? null;
    case 'reports':
      return u.fullReportsCount ?? u.reportsCount ?? 0;
    case 'created':
      return u.creationTime ? Date.parse(u.creationTime) : null;
    case 'lastLogin':
      return u.lastSignInTime ? Date.parse(u.lastSignInTime) : null;
  }
}

export function UsersList() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [pageToken, setPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [suspFilter, setSuspFilter] = useState<'all' | 'only' | 'hide'>('all');

  async function loadPage(token: string | null, append: boolean) {
    setLoading(true);
    setErr(null);
    try {
      const res = await callListUsers({ pageToken: token ?? undefined, pageSize: 200 });
      setUsers((prev) => (append ? [...prev, ...res.data.users] : res.data.users));
      setPageToken(res.data.nextPageToken);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPage(null, false);
  }, []);

  const suspiciousCount = useMemo(() => users.filter(isSuspiciousUser).length, [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (suspFilter !== 'all') {
        const susp = isSuspiciousUser(u);
        if (suspFilter === 'only' && !susp) return false;
        if (suspFilter === 'hide' && susp) return false;
      }
      if (!q) return true;
      return (
        u.email?.toLowerCase().includes(q) ||
        u.displayName?.toLowerCase().includes(q) ||
        u.uid.toLowerCase().includes(q)
      );
    });
  }, [users, query, suspFilter]);

  const { sorted, toggle, arrow } = useSort<AdminUser, SortKey>(filtered, sortValue);

  return (
    <div>
      <div className="row between" style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Usuarios ({users.length})</h2>
        <div className="row" style={{ gap: 8 }}>
          <button
            onClick={() => downloadCsv(csvFilename('usuarios'), buildUsersCsv(sorted))}
            disabled={sorted.length === 0}
          >
            Exportar CSV
          </button>
          <button onClick={() => loadPage(null, false)} disabled={loading}>
            {loading ? 'Cargando…' : 'Refrescar'}
          </button>
        </div>
      </div>

      <div className="search-bar row" style={{ gap: 12, alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Buscar por email, nombre o UID…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1 }}
        />
        <label
          className="row"
          style={{ gap: 6, alignItems: 'center', whiteSpace: 'nowrap' }}
          title="Email tipo nombreapellido.NNNNN@gmail.com y sin actividad — probable bot"
        >
          Sospechosos ({suspiciousCount}):
          <select value={suspFilter} onChange={(e) => setSuspFilter(e.target.value as 'all' | 'only' | 'hide')}>
            <option value="all">Mostrar todos</option>
            <option value="only">Solo sospechosos</option>
            <option value="hide">Ocultar sospechosos</option>
          </select>
        </label>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="panel" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th className="sortable" onClick={() => toggle('name')}>Usuario{arrow('name')}</th>
              <th className="sortable" onClick={() => toggle('email')}>Email{arrow('email')}</th>
              <th className="sortable num" onClick={() => toggle('credits')}>Créditos{arrow('credits')}</th>
              <th className="sortable num" onClick={() => toggle('reports')}>Informes{arrow('reports')}</th>
              <th className="sortable" onClick={() => toggle('created')}>Creado{arrow('created')}</th>
              <th className="sortable" onClick={() => toggle('lastLogin')}>Último login{arrow('lastLogin')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.uid}>
                <td>
                  <Link to={`/users/${u.uid}`}>{u.displayName || '(sin nombre)'}</Link>{' '}
                  {u.isAdmin && <span className="badge admin">admin</span>}
                  {u.disabled && <span className="badge disabled">disabled</span>}
                  {isSuspiciousUser(u) && (
                    <span className="badge suspicious" title="Email tipo nombreapellido.NNNNN@gmail.com y sin actividad — probable bot">
                      sospechoso
                    </span>
                  )}
                </td>
                <td>{u.email || '—'}</td>
                <td className="num">{u.credits ?? '—'}</td>
                <td className="num">{u.fullReportsCount ?? u.reportsCount ?? 0}</td>
                <td className="muted">{formatDate(u.creationTime)}</td>
                <td className="muted">{formatDate(u.lastSignInTime)}</td>
                <td>
                  <Link to={`/users/${u.uid}`}>
                    <button>Ver →</button>
                  </Link>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 32 }}>
                  Sin resultados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageToken && (
        <div className="pagination">
          <button onClick={() => loadPage(pageToken, true)} disabled={loading}>
            Cargar más
          </button>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}
